import axios from "axios";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { SelectOptionType } from "types/select/SelectOptionType";
import Select, { MultiValue, SingleValue } from "react-select";
import _lodash from "lodash";
import { ScaleLoader } from "react-spinners";
import { Tooltip } from "antd";
import makeAnimated from "react-select/animated";
import { toast } from "react-hot-toast";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { CustomLabel } from "../input/CustomLabel";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";

type CustomMultiSelectAsyncPropsWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
};

type CustomMultiSelectAsyncPropsWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validacoes*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
};

type CustomMultiSelectAsyncProps = {
  id: string;
  nomeAtributoValue: string;
  nomeAtributoLabel: string;
  customNomeAtributoLabelSufix?: string;
  customNomeAtributoLabelPrefix?: string;
  placeholder?: string;
  endpoint: string;
  tooltip?: string;
  invalidateQuerysKey?: any[];
  className?: string;
  label?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  /*** Quando algum item da lista sofrer uma alteração de valor, o endpoint de buscar opções será chamado novamente quando o usuário abrir o select */
  atributosRefresh?: any[];
  possuiErro?: boolean;
  required?: boolean;
  onChange?: (opcaoSelecionada: MultiValue<SelectOptionType<any>>) => void;
  defaultValue?: SingleValue<SelectOptionType<any>>;
} & (CustomMultiSelectAsyncPropsWithoutValidationProps | CustomMultiSelectAsyncPropsWithValidationProps);

const CustomMultiSelectAsync: React.FC<CustomMultiSelectAsyncProps> = (props) => {
  const prevProps = useRef<CustomMultiSelectAsyncProps>();
  const [options, setOptions] = useState<SelectOptionType<any>[]>([]);
  const [deveBuscarOpcoes, setDeveBuscarOpcoes] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (prevProps && prevProps.current && prevProps.current !== props) {
      if (atributosRefreshSofreuAlteracao(prevProps.current, props)) {
        setDeveBuscarOpcoes(true);
      }
    }
    prevProps.current = props;
  });

  const atributosRefreshSofreuAlteracao = (
    prevProps: CustomMultiSelectAsyncProps,
    props: CustomMultiSelectAsyncProps
  ) => {
    const prevAtributesRefresh = prevProps.atributosRefresh;
    const atributesRefreshAtuais = props.atributosRefresh;

    if (prevAtributesRefresh?.length !== atributesRefreshAtuais?.length) {
      return true;
    }

    return !_lodash.isEqual(prevAtributesRefresh, atributesRefreshAtuais);
  };

  const customMenuOpen = async () => {
    if (deveBuscarOpcoes) {
      setOptions([]);
      await loadOptions();
      setDeveBuscarOpcoes(false);
    }
  };

  const loadOptions = async () => {
    setIsLoading(true);
    await axios
      .get(props.endpoint)
      .then((resp) => {
        const registros = resp.data;
        const options: SelectOptionType<any>[] = registros.map((registro: any) => ({
          value: registro[props.nomeAtributoValue],
          label:
            (props.customNomeAtributoLabelPrefix ? props.customNomeAtributoLabelPrefix : "") +
            registro[props.nomeAtributoLabel] +
            (props.customNomeAtributoLabelSufix ? props.customNomeAtributoLabelSufix : ""),
        }));
        setOptions(options);
      })
      .catch((error) => toast.error("Erro ao buscar opções"))
      .finally(() => setIsLoading(false));
  };

  const renderLoadingMessage = () => {
    return (
      <div>
        <span style={{ fontWeight: "bold" }}>Carregando opções</span>
        <ScaleLoader height="10px" color="#83cf9c" />
      </div>
    );
  };

  const getCustomStyle = (possuiErro: boolean): object => {
    return {
      control: (provided: any, state: any) => ({
        ...provided,
        borderColor: possuiErro ? "#dc3545 !important" : state.isFocused ? "#83cf9c !important" : "#c4c4c4",
        boxShadow: "none",
        fontSize: "small",
        fontWeight: "200",
      }),
    };
  };

  const renderCustomMultiSelectAsyncComValidacao = (): ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && (
                <CustomLabel possuiErro={fieldState.error != null}>
                  <div className="d-flex">
                    <div className="me-2">{props.required ? `${props.label} *` : props.label}</div>
                    <div>
                      {props.tooltip && (
                        <Tooltip title={props.tooltip}>
                          <FaQuestionCircle size={15} />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </CustomLabel>
              )}
              <Select
                {...props.register(props.name)}
                {...field}
                id={props.id}
                styles={getCustomStyle(fieldState.error != null)}
                isClearable={props.isClearable}
                blurInputOnSelect
                closeMenuOnSelect
                required={props.required}
                components={makeAnimated()}
                className={props.className || ""}
                isMulti
                isDisabled={props.isDisabled}
                placeholder={props.placeholder || ""}
                onMenuOpen={() => customMenuOpen()}
                isLoading={isLoading}
                loadingMessage={() => renderLoadingMessage()}
                noOptionsMessage={() => "Nenhum registro foi encontrado"}
                options={options}
                value={field.value || null}
                onChange={(opcaoSelecionada: MultiValue<SelectOptionType<any>>) => {
                  field.onChange(opcaoSelecionada);
                  props.setValue(props.name, opcaoSelecionada);
                  props.onChange && props.onChange(opcaoSelecionada);
                }}
              />
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
            </>
          )}
        />
      );
    }
  };

  const renderCustomMultiSelectAsyncSemValidacao = (): ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && (
            <CustomLabel possuiErro={false}>
              <div className="d-flex">
                <div className="me-2">{props.required ? `${props.label} *` : props.label}</div>
                <div>
                  {props.tooltip && (
                    <Tooltip title={props.tooltip}>
                      <FaQuestionCircle size={15} />
                    </Tooltip>
                  )}
                </div>
              </div>
            </CustomLabel>
          )}
          <Select
            id={props.id}
            styles={getCustomStyle(false)}
            isClearable={props.isClearable}
            blurInputOnSelect
            closeMenuOnSelect
            className={props.className || ""}
            isDisabled={props.isDisabled}
            placeholder={props.placeholder || ""}
            isMulti
            required={props.required}
            onMenuOpen={() => customMenuOpen()}
            isLoading={isLoading}
            loadingMessage={() => renderLoadingMessage()}
            noOptionsMessage={() => "Nenhum registro foi encontrado"}
            options={options}
            onChange={(opcaoSelecionada: MultiValue<SelectOptionType<any>>) =>
              props.onChange && props.onChange(opcaoSelecionada)
            }
            defaultValue={props.defaultValue}
          />
        </>
      );
    }
  };

  return (
    <>
      {props.usarValidacaoParaFormulario && renderCustomMultiSelectAsyncComValidacao()}
      {!props.usarValidacaoParaFormulario && renderCustomMultiSelectAsyncSemValidacao()}
    </>
  );
};

export default CustomMultiSelectAsync;
